<template>
  <div class="minimap-container" :class="{ minimized }" :style="containerStyle">
    <div class="minimap-header" @mousedown="startPanelDrag" title="拖拽移动">
      <span class="minimap-title">小地图</span>
      <button class="minimize-btn" @click.stop="minimized = !minimized">{{ minimized ? '□' : '－' }}</button>
    </div>
    <div v-show="!minimized" class="minimap-body">
      <canvas
        ref="canvasRef"
        :width="MAP_SIZE"
        :height="MAP_SIZE"
        class="minimap-canvas"
        title="框内拖拽平移视角 · 四角拖拽缩放 · 空白处点击定位 · 滚轮缩放"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
        @wheel.prevent="onWheel"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { MinimapRenderer } from '../three/MinimapRenderer'
import type { SceneManager } from '../three/SceneManager'

const MAP_SIZE = 180

const props = defineProps<{
  sceneManager: SceneManager | null
}>()

const emit = defineEmits<{
  navigate: [x: number, z: number]
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const minimized = ref(false)

// 拖动面板位置
const hasDragged = ref(false)
const panelLeft = ref(0)
const panelTop = ref(0)

const containerStyle = computed(() => {
  if (!hasDragged.value) return {}
  return {
    left: `${panelLeft.value}px`,
    top: `${panelTop.value}px`,
    bottom: 'auto',
    right: 'auto',
  }
})

function startPanelDrag(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('.minimize-btn')) return
  e.preventDefault()

  const el = (e.currentTarget as HTMLElement).closest('.minimap-container') as HTMLElement
  const parent = el.parentElement!
  const parentRect = parent.getBoundingClientRect()
  const elRect = el.getBoundingClientRect()

  const offsetX = e.clientX - elRect.left
  const offsetY = e.clientY - elRect.top

  // 初始化绝对坐标（首次拖动时从当前屏幕位置推算）
  if (!hasDragged.value) {
    panelLeft.value = elRect.left - parentRect.left
    panelTop.value = elRect.top - parentRect.top
    hasDragged.value = true
  }

  function onMove(ev: MouseEvent) {
    const newLeft = ev.clientX - parentRect.left - offsetX
    const newTop = ev.clientY - parentRect.top - offsetY
    panelLeft.value = Math.max(0, Math.min(parentRect.width - elRect.width, newLeft))
    panelTop.value = Math.max(0, Math.min(parentRect.height - 28, newTop))
  }

  function onUp() {
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
  }

  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
}

let minimapRenderer: MinimapRenderer | null = null
let rafId = 0
type DragKind = 'none' | 'navigate' | 'pan' | 'zoom'
let dragKind: DragKind = 'none'
let lastCx = 0
let lastCy = 0
let zoomStartClientY = 0
let zoomStartCamY = 0

onMounted(() => {
  if (!canvasRef.value || !props.sceneManager) return
  minimapRenderer = new MinimapRenderer(props.sceneManager, canvasRef.value)

  const loop = (time: number) => {
    minimapRenderer?.tick(time)
    rafId = requestAnimationFrame(loop)
  }
  rafId = requestAnimationFrame(loop)
})

onUnmounted(() => {
  cancelAnimationFrame(rafId)
  minimapRenderer?.dispose()
})

function navigateTo(e: MouseEvent) {
  if (!minimapRenderer || !canvasRef.value) return
  const rect = canvasRef.value.getBoundingClientRect()
  const cx = e.clientX - rect.left
  const cy = e.clientY - rect.top
  const world = minimapRenderer.canvasToWorld(cx, cy)
  emit('navigate', world.x, world.z)
}

function onWheel(e: WheelEvent) {
  const sm = props.sceneManager
  if (!sm || !minimapRenderer) return
  const factor = e.deltaY > 0 ? 1.06 : 1 / 1.06
  const cam = sm.camera
  const newY = Math.max(12, Math.min(800, cam.position.y * factor))
  cam.position.y = newY
  sm.controls.update()
  minimapRenderer.markDirty()
}

function onMouseDown(e: MouseEvent) {
  if (!minimapRenderer || !canvasRef.value || !props.sceneManager) return
  const rect = canvasRef.value.getBoundingClientRect()
  const cx = e.clientX - rect.left
  const cy = e.clientY - rect.top
  const pick = minimapRenderer.pickMinimapInteraction(cx, cy)
  if (pick === 'navigate') {
    dragKind = 'navigate'
    navigateTo(e)
  } else if (pick === 'pan') {
    dragKind = 'pan'
    lastCx = cx
    lastCy = cy
  } else {
    dragKind = 'zoom'
    zoomStartClientY = e.clientY
    zoomStartCamY = props.sceneManager.camera.position.y
  }
}

function onMouseMove(e: MouseEvent) {
  if (dragKind === 'none' || !minimapRenderer || !canvasRef.value) return
  const sm = props.sceneManager
  const rect = canvasRef.value.getBoundingClientRect()
  const cx = e.clientX - rect.left
  const cy = e.clientY - rect.top

  if (dragKind === 'navigate') {
    navigateTo(e)
    return
  }

  if (dragKind === 'pan' && sm) {
    const w0 = minimapRenderer.canvasToWorld(lastCx, lastCy)
    const w1 = minimapRenderer.canvasToWorld(cx, cy)
    const dx = w1.x - w0.x
    const dz = w1.z - w0.z
    sm.controls.target.x += dx
    sm.controls.target.z += dz
    sm.camera.position.x += dx
    sm.camera.position.z += dz
    sm.controls.update()
    minimapRenderer.markDirty()
    lastCx = cx
    lastCy = cy
    return
  }

  if (dragKind === 'zoom' && sm) {
    const dy = e.clientY - zoomStartClientY
    const newY = Math.max(12, Math.min(800, zoomStartCamY * Math.exp(dy * 0.004)))
    sm.camera.position.y = newY
    sm.controls.update()
    minimapRenderer.markDirty()
  }
}

function onMouseUp() {
  dragKind = 'none'
}

// 暴露给父组件调用
defineExpose({
  markDirty: () => minimapRenderer?.markDirty()
})
</script>

<style scoped>
.minimap-container {
  position: absolute;
  bottom: 8px;
  left: 12px;
  z-index: var(--z-sys-minimap);
  background: #111822;
  border: 1px solid #2a3a50;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
}

.minimap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  background: #1a2535;
  border-bottom: 1px solid #2a3a50;
  cursor: move;
  user-select: none;
}

.minimap-title {
  font-size: 11px;
  color: #7799bb;
}

.minimize-btn {
  background: transparent;
  border: none;
  color: #556677;
  cursor: pointer;
  font-size: 12px;
  padding: 0 2px;
  line-height: 1;
}

.minimize-btn:hover {
  color: #aabbcc;
}

.minimap-body {
  padding: 0;
}

.minimap-canvas {
  display: block;
  width: 180px;
  height: 180px;
  cursor: grab;
}

.minimap-canvas:active {
  cursor: grabbing;
}

.minimap-container.minimized .minimap-body {
  display: none;
}
</style>
